import type { Advisory } from "../../lib/qa";
import type { ReviewVerdict } from "../../lib/types";

/**
 * ABOUT THIS RUN — the advisory findings that belong to the whole document, not one block.
 *
 * Two sources, both run-level: QA issues the backend did not anchor to a block, and the Gemini-Pro
 * review panel (five lenses — editorial, faithfulness, audience-fit, research-use, risk), whose
 * verdicts are inherently run-level (a lens name, no block). All of it is ADVISORY: it informs the
 * operator and never blocks export. `reviewSummary` is the one-line read at the top.
 */
const LENS_LABELS: Record<string, string> = {
  editorial: "Editorial & tone",
  faithfulness: "Faithfulness",
  "audience-fit": "Audience fit",
  "research-use": "Research use",
  risk: "Risk"
};

export function RunTray({
  summary,
  advisories,
  panel
}: {
  summary?: string;
  advisories: Advisory[];
  panel: ReviewVerdict[];
}) {
  if (!summary && !advisories.length && !panel.length) return null;
  return (
    <section className="run-tray" data-testid="run-tray">
      <div className="run-tray-head">
        <h2>About this run</h2>
        <p>Advisory only — none of this blocks export.</p>
      </div>

      {summary ? <p className="run-tray-summary">{summary}</p> : null}

      {advisories.length ? (
        <ul className="run-tray-list">
          {advisories.map((advisory, index) => (
            <li key={`${advisory.gateId}-${index}`} className="run-tray-item">
              <span className="run-tray-chip">{advisory.gateLabel}</span>
              <span className="run-tray-note">{advisory.message}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {panel.length ? (
        <ul className="run-tray-panel">
          {panel.map((reviewer) => (
            <li key={reviewer.role} className="run-tray-review">
              <div className="run-tray-review-head">
                <span className="run-tray-role">{LENS_LABELS[reviewer.role] ?? reviewer.role}</span>
                <span className="run-tray-verdict" data-verdict={reviewer.verdict}>
                  {reviewer.verdict}
                </span>
              </div>
              <p className="run-tray-review-note">{reviewer.notes}</p>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
